import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";
import { t } from "ttag";

import { usePortalUser } from "@/app/hooks";
import { ROUTES } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import { consultationsQueriesStore } from "@/features/consultations/api";
import { FORMS_ROUTE } from "@/features/forms/router";
import { Button, DropdownMenu, VideoCameraIcon } from "@/shared/ui";

const triggerVariants = tv({
  slots: {
    triggerContainer: "",
    trigger: "",
  },
  variants: {
    position: {
      header: {
        triggerContainer: "hidden md:flex",
        trigger: "",
      },
      body: {
        triggerContainer:
          "sticky bottom-0 z-10 -m-6 flex bg-white px-6 py-5 shadow-modal-footer md:hidden",
        trigger: "grow",
      },
    },
  },
});
type TriggerVariants = VariantProps<typeof triggerVariants>;

export const BookOneOnOneDropdownMenu = ({
  position = "body",
}: TriggerVariants) => {
  const canScheduleJourneySupport = usePortalUserStore(
    (state) => state.portalUser?.journeySupport?.canSchedule,
  );
  const canAccessNutritionalCounseling = usePortalUserStore(
    (state) => state.canAccessNutritionalCounseling,
  );

  const { isFetching: isFetchingPortalUser } = usePortalUser();

  const navigate = useNavigate();

  const handleClick = () => {
    navigate(ROUTES.COACHING.JOURNEY_SUPPORT_SCHEDULE);
  };

  const {
    data: dietitianAppointments,
    isFetching: isFetchingDietitianAppointments,
  } = useQuery(
    consultationsQueriesStore.consultations.appointments._ctx.list._ctx.upcoming
      ._ctx.dietitian,
  );

  const isBookingJourneySupportDisabled =
    !canScheduleJourneySupport || isFetchingPortalUser;

  const isBookingDietitianDisabled =
    Boolean(dietitianAppointments?.length) ||
    isFetchingDietitianAppointments ||
    !canAccessNutritionalCounseling;

  const { triggerContainer, trigger } = triggerVariants({ position });

  return (
    <DropdownMenu.Root modal={false}>
      <div className={triggerContainer()}>
        <DropdownMenu.Trigger asChild className={trigger()}>
          <Button size="lg">
            <VideoCameraIcon className="size-4" />
            {t`Book 1-on-1`}
          </Button>
        </DropdownMenu.Trigger>
      </div>

      <DropdownMenu.Content>
        <DropdownMenu.Item disabled={isBookingDietitianDisabled} asChild>
          <Link to={FORMS_ROUTE.DIETITIAN}>{t`Dietitian visit`}</Link>
        </DropdownMenu.Item>

        <DropdownMenu.Separator />

        <DropdownMenu.Item
          disabled={isBookingJourneySupportDisabled}
          onClick={handleClick}
        >
          {t`Journey support`}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
